import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from './icons/Icon';
import './OpenInMenu.css';

// The IDE items are switched off ("Coming soon") until mia-desktop handles `open-project` with an `ide`; drop `disabled` on
// them to turn them back on.
// `miaflowdesktop://` is mia-desktop's own registered LaunchServices scheme
// (see mia-desktop's URLSchemeCallbackReceiver.swift — already used for its
// Keycloak OAuth redirect). `open-project` is a NEW host this menu adds:
// mia-desktop's own `.onOpenURL` needs a matching handler for it (binds the
// app to this tenant/project and, when `ide` is present, opens that
// project's locally-synced folder — SyncEngine.swift — in the requested
// external editor).
const DESKTOP_SCHEME = 'miaflowdesktop';

// `miaflow://` is mia-flow-ios's own registered scheme (confirmed via its
// AppEnvironment.swift redirectURI, already used for Keycloak OAuth). Like
// mia-desktop, it currently only handles `oauth/callback` — an `open-project`
// host is a NEW handler the iOS app still needs to add. A desktop browser
// can't launch an app on a separate physical phone directly, so this mirrors
// the "Open in Flow CLI" pattern: copy a ready-to-open link instead.
const MOBILE_SCHEME = 'miaflow';

// Real brand marks, ported from ai-foundry-website's own connections page
// (src/components/{Vscode,Anthropic,Kiro,Cursor,JetBrains,Antigravity}Icon.tsx)
// per live direction ("AI Foundry Website has already all icons for the IDE,
// use it") rather than inventing placeholder colored squares. Kept
// co-located here (not added to the shared icons/Icon.jsx map) since this
// menu is their only consumer in this repo.
const VscodeIcon = ({ size }) => (
  <svg fill="#007ACC" height={size} viewBox="0 0 24 24" width={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M23.15 2.587L18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L3.899 12 .326 15.26a1 1 0 0 0 .001 1.479L1.65 17.94a.999.999 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 19.841V4.16a1.5 1.5 0 0 0-.85-1.573zm-5.146 14.861L10.826 12l7.178-5.448v10.896z" />
  </svg>
);

const AnthropicIcon = ({ size }) => (
  <svg fill="#CC785C" height={size} viewBox="0 0 24 24" width={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z" />
  </svg>
);

const KiroIcon = ({ size }) => (
  <svg fill="#FF9900" height={size} viewBox="0 0 24 24" width={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M4 3h3.5v7.5L14 3h5l-7 9 7 9h-5l-6.5-7.5V21H4V3z" />
  </svg>
);

const CursorIcon = ({ size }) => (
  <svg fill="#000000" height={size} viewBox="0 0 24 24" width={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M11.503.131 1.891 5.678a.84.84 0 0 0-.42.726v11.188c0 .3.162.575.42.724l9.609 5.55a1 1 0 0 0 .998 0l9.61-5.55a.84.84 0 0 0 .42-.724V6.404a.84.84 0 0 0-.42-.726L12.497.131a1.01 1.01 0 0 0-.996 0M2.657 6.338h18.55c.263 0 .43.287.297.515L12.23 22.918c-.062.107-.229.064-.229-.06V12.335a.59.59 0 0 0-.295-.51l-9.11-5.257c-.109-.063-.064-.23.061-.23" />
  </svg>
);

const JetBrainsIcon = ({ size }) => (
  <svg fill="#000000" height={size} viewBox="0 0 24 24" width={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M2.345 23.997A2.347 2.347 0 0 1 0 21.652V10.988C0 9.665.535 8.37 1.473 7.433l5.965-5.961A5.01 5.01 0 0 1 10.989 0h10.666A2.347 2.347 0 0 1 24 2.345v10.664a5.056 5.056 0 0 1-1.473 3.554l-5.965 5.965A5.017 5.017 0 0 1 13.007 24v-.003H2.345Zm8.969-6.854H5.486v1.371h5.828v-1.371ZM3.963 6.514h13.523v13.519l4.257-4.257a3.936 3.936 0 0 0 1.146-2.767V2.345c0-.678-.552-1.234-1.234-1.234H10.989a3.897 3.897 0 0 0-2.767 1.145L3.963 6.514Zm-.192.192L2.256 8.22a3.944 3.944 0 0 0-1.145 2.768v10.664c0 .678.552 1.234 1.234 1.234h10.666a3.9 3.9 0 0 0 2.767-1.146l1.512-1.511H3.771V6.706Z" />
  </svg>
);

const AntigravityIcon = ({ size }) => (
  <svg fill="#3186FF" height={size} viewBox="9 18 93 80" width={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M89.6992 93.695C94.3659 97.195 101.366 94.8617 94.9492 88.445C75.6992 69.7783 79.7825 18.445 55.8659 18.445C31.9492 18.445 36.0325 69.7783 16.7825 88.445C9.78251 95.445 17.3658 97.195 22.0325 93.695C40.1159 81.445 38.9492 59.8617 55.8659 59.8617C72.7825 59.8617 71.6159 81.445 89.6992 93.695Z" />
  </svg>
);

const IDE_ITEMS = [
  { slug: 'vscode', label: 'VS Code', Icon: VscodeIcon },
  { slug: 'cursor', label: 'Cursor', Icon: CursorIcon },
  { slug: 'kiro', label: 'Kiro', Icon: KiroIcon },
  { slug: 'claude-code', label: 'Claude Code', Icon: AnthropicIcon },
  { slug: 'intellij', label: 'IntelliJ IDEA', Icon: JetBrainsIcon },
  { slug: 'antigravity', label: 'Antigravity', Icon: AntigravityIcon },
];

function buildDesktopUrl(tenantId, projectId, ide) {
  const params = new URLSearchParams({ tenantId: tenantId || '', projectId: projectId || '' });
  if (ide) { params.set('ide', ide); }
  return `${DESKTOP_SCHEME}://open-project?${params.toString()}`;
}

// Mirrors the existing `show-toast` window-event pattern used elsewhere in
// this app (VfsChatPanel's sending overlay, App.jsx's banner) rather than
// introducing a second toast mechanism.
function showToast(message, type = 'success') {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
}

/**
 * Split button: the primary action (passed in as `onOpenApp`, e.g. "open
 * the live preview in a new tab") stays a single click, exactly as before
 * this existed; the chevron reveals ways to open THIS project somewhere
 * else entirely. Every project here is vfs-backed (no local folder of its
 * own in mia-flow-fe), so every item below routes through an app that
 * already knows how to reach it: Flow Desktop opens directly via its own
 * registered URL scheme (and can sync a local copy — see SyncEngine.swift);
 * the IDE items reuse that exact same scheme with an `ide` hint, since none
 * of VS Code/Cursor/Kiro/Claude Code/IntelliJ/Antigravity know how to reach
 * vfs-service themselves — mia-desktop is the one app in this system that
 * already keeps a local, synced copy on disk to point them at. Flow CLI has
 * no OS-registered URL scheme (it authenticates via a loopback HTTP server,
 * not a custom scheme — see mia-flow-cli's KeycloakAuth) and its own
 * `/project`/`/resume` commands are interactive REPL input, not process
 * arguments, so its item copies a ready command instead of attempting a
 * launch that can't exist yet.
 */
export default function OpenInMenu({
  tenantId,
  projectId,
  onOpenApp,
  // REAL BUG this fixes (live-reported: the button rendered with no
  // visible icon/label at all): this app runs React 19, which dropped
  // `defaultProps` support for function components entirely — the
  // `OpenInMenu.defaultProps` block below used to declare these same
  // defaults did nothing at runtime, so every call site that didn't pass
  // openAppLabel/openAppTitle explicitly (all of them) got `undefined`
  // instead. JS default parameters (React 19's own replacement) actually
  // work.
  openAppLabel = 'Open App',
  openAppTitle = 'Open application in new tab',
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) { return undefined; }
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const openDesktop = (ide) => {
    setIsOpen(false);
    window.location.href = buildDesktopUrl(tenantId, projectId, ide);
  };

  const openCli = async () => {
    setIsOpen(false);
    const command = `flow\n/project ${projectId}`;
    try {
      await navigator.clipboard.writeText(command);
      showToast('Command copied — paste it in your terminal to open this project in Flow CLI');
    } catch {
      showToast(`Run this in your terminal: flow, then /project ${projectId}`, 'info');
    }
  };

  const sendToMobile = async () => {
    setIsOpen(false);
    const params = new URLSearchParams({ tenantId: tenantId || '', projectId: projectId || '' });
    const link = `${MOBILE_SCHEME}://open-project?${params.toString()}`;
    try {
      await navigator.clipboard.writeText(link);
      showToast('Link copied — open it on your phone to launch this project in Flow Mobile App');
    } catch {
      showToast(`Open this link on your phone: ${link}`, 'info');
    }
  };

  return (
    <div className="open-in-menu" ref={rootRef}>
      <button
        className="open-in-menu-primary"
        disabled={disabled}
        onClick={onOpenApp}
        title={openAppTitle}
        type="button"
      >
        <Icon name="link" size={14} />
        {openAppLabel}
      </button>
      {/* Deliberately NOT gated on `disabled` — Desktop/CLI/IDE items below
          don't need a live preview tab to be useful (unlike the primary
          "Open App" browser action, which does), so the chevron stays
          clickable even when there's nothing running yet — e.g. on the
          Dashboard tab, or before this service's preview has been built. */}
      <button
        aria-expanded={isOpen}
        aria-label="More ways to open this project"
        className="open-in-menu-chevron"
        onClick={() => setIsOpen((v) => !v)}
        type="button"
      >
        <Icon name="chevron-down" size={14} />
      </button>

      {isOpen && (
        <div className="open-in-menu-dropdown" role="menu">
          {/* Flow's own products — the real Mia-Platform mark (per live
              direction "for the Flow use the icons of mia"), the same one
              icons/Icon.jsx's own `mia-platform` entry already draws. */}
          <button className="open-in-menu-item" onClick={() => openDesktop()} role="menuitem" type="button">
            <Icon name="mia-platform" size={16} />
            <span>Open in Flow Desktop</span>
          </button>
          <button className="open-in-menu-item" onClick={openCli} role="menuitem" type="button">
            <Icon name="mia-platform" size={16} />
            <span>Open in Flow CLI</span>
          </button>
          <button className="open-in-menu-item" onClick={sendToMobile} role="menuitem" type="button">
            <Icon name="mia-platform" size={16} />
            <span>Send to Flow Mobile App</span>
          </button>

          <div className="open-in-menu-divider" />
          <div className="open-in-menu-section-label">Open in IDE</div>

          {IDE_ITEMS.map(({ slug, label, Icon: IdeIcon }) => (
            <button
              aria-disabled="true"
              className="open-in-menu-item open-in-menu-item-soon"
              disabled
              key={slug}
              onClick={() => openDesktop(slug)}
              role="menuitem"
              type="button"
            >
              <IdeIcon size={16} />
              <span>{label}</span>
              <span className="open-in-menu-soon">Coming soon</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

OpenInMenu.propTypes = {
  tenantId: PropTypes.string,
  projectId: PropTypes.string,
  onOpenApp: PropTypes.func.isRequired,
  openAppLabel: PropTypes.string,
  openAppTitle: PropTypes.string,
  disabled: PropTypes.bool,
};
